import type { ICitation } from 'src/types/citation';

import useSWR, { mutate } from 'swr';
import { useMemo, useState, useCallback } from 'react';

import {
  poster,
  fetcher,
  deleter,
  updater,
  getEndpoints,
  updateEndpoints,
  createEndpoints,
  deleteEndpoints,
} from 'src/utils/axios';

// ----------------------------------------------------------------------

const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

// ----------------------------------------------------------------------

type CitationsData = {
  data: ICitation[];
};

export type CitationForType = 'place' | 'story' | 'image';

// Private function to handle citations retrieval, not exported
export function useGetCitations(type: CitationForType, guid: string | null) {
  const endpointUrl = guid ? getEndpoints[type].citations(guid) : null;
  const { data, isLoading, error, isValidating } = useSWR<CitationsData>(
    endpointUrl,
    fetcher,
    swrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      citations: data?.data || [],
      citationsLoading: isLoading,
      citationsError: error,
      citationsValidating: isValidating,
      citationsEmpty: !isLoading && (!data?.data || data?.data.length === 0),
    }),
    [data?.data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useAddCitation(type: CitationForType) {
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addCitation = useCallback(
    async (citation: { details: string; url: string; body?: string }, guid: string) => {
      setIsAdding(true);
      setError(null);

      try {
        const formData = new FormData();
        formData.append('details', citation.details);
        formData.append('url', citation.url);
        formData.append('body', citation.body || '');

        const endpoint = createEndpoints[type].citation(guid);
        const result = await poster(endpoint, formData);

        // Revalidate the citations list
        await mutate(getEndpoints[type].citations(guid));

        return result.data;
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setIsAdding(false);
      }
    },
    [type]
  );

  return useMemo(
    () => ({
      addCitation,
      isAdding,
      error,
    }),
    [addCitation, isAdding, error]
  );
}

export function useDeleteCitation(type: CitationForType, guid: string) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteCitation = useCallback(
    async (citationGuid: string) => {
      setIsDeleting(true);
      setError(null);

      try {
        const endpoint = deleteEndpoints.citation.citation(citationGuid);
        const result = await deleter(endpoint);

        // Revalidate the citations list
        await mutate(getEndpoints[type].citations(guid));

        return result.data;
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setIsDeleting(false);
      }
    },
    [type, guid]
  );

  return useMemo(
    () => ({
      deleteCitation,
      isDeleting,
      error,
    }),
    [deleteCitation, isDeleting, error]
  );
}

export function useUpdateCitation(type: CitationForType, guid: string) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateCitation = useCallback(
    async (citationGuid: string, citation: { details: string; url: string; body?: string }) => {
      setIsUpdating(true);
      setError(null);

      try {
        const formData = new FormData();
        formData.append('details', citation.details);
        formData.append('url', citation.url);
        formData.append('body', citation.body || '');

        const endpoint = updateEndpoints.citation.citation(citationGuid);
        const result = await updater(endpoint, formData);

        // Revalidate the citations list
        await mutate(getEndpoints[type].citations(guid));

        return result.data;
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setIsUpdating(false);
      }
    },
    [type, guid]
  );

  return useMemo(
    () => ({
      updateCitation,
      isUpdating,
      error,
    }),
    [updateCitation, isUpdating, error]
  );
}
